// src/pages/Profile/Profile.jsx
import React, { useEffect, useState } from "react";
import "./Profile.css";
import { getUser } from "../Login/authUtils"; // ✅ using userDetails

const FALLBACK_AVATAR_BG = "#2563eb";

// Build a safe photo URL (handles relative & absolute)
function getPhotoUrl(user) {
  if (!user?.photo) return null;

  // If already an absolute URL (like your example)
  if (/^https?:\/\//i.test(user.photo)) return user.photo;

  // Otherwise assume backend serves static files
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${base}/${user.photo.replace(/^\/+/, "")}`;
}

// In case userDetails is stored as { user, token } or { data: { user } }
function normalizeUser(raw) {
  if (!raw) return null;
  if (raw.user) return raw.user;
  if (raw.data?.user) return raw.data.user;
  return raw;
}

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const raw = getUser(); // reads from localStorage("userDetails")
    const normalized = normalizeUser(raw);
    setUser(normalized || null);
    setLoaded(true);
  }, []);

  if (!loaded) {
    // optional: small loading state instead of flashing "No user data"
    return (
      <div className="profile-page">
        <div className="profile-card profile-card--empty">
          <p>Loading profile…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-page">
        <div className="profile-card profile-card--empty">
          <p>No user data found. Please log in again.</p>
        </div>
      </div>
    );
  }

  const photoUrl = getPhotoUrl(user);
  const initials =
    user.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "U";

  const isActive = user.isActive !== false;
  const isApproved = !!user.isApproved;

  return (
    <div className="profile-page">
      <div className="profile-card">
        {/* LEFT: Avatar */}
        <div className="profile-avatar-wrapper">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={user.name}
              className="profile-avatar-img"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div
              className="profile-avatar-fallback"
              style={{ background: FALLBACK_AVATAR_BG }}
            >
              {initials}
            </div>
          )}
        </div>

        {/* RIGHT: Info */}
        <div className="profile-main">
          <div className="profile-main-header">
            <h1 className="profile-name">{user.name}</h1>

            {user.designation && (
              <p className="profile-designation">{user.designation}</p>
            )}

            {user.role && (
              <p className="profile-role">
                {typeof user.role === "string"
                  ? `Role: ${user.role}`
                  : user.role.roleName || "Role User"}
              </p>
            )}
          </div>

          <div className="profile-badges">
            <span
              className={`badge badge-dot ${
                isActive ? "badge-success" : "badge-muted"
              }`}
            >
              {isActive ? "Active" : "Inactive"}
            </span>

            <span
              className={`badge badge-soft ${
                isApproved ? "badge-approve" : "badge-pending"
              }`}
            >
              {isApproved ? "Approved" : "Pending Approval"}
            </span>

            {user.userId && (
              <span className="badge badge-outline">{user.userId}</span>
            )}
          </div>

          <div className="profile-separator" />

          {/* Contact + meta */}
          <div className="profile-grid">
            <div className="profile-field">
              <span className="profile-label">Email</span>
              <span className="profile-value">{user.emailId}</span>
            </div>
            <div className="profile-field">
              <span className="profile-label">Phone</span>
              <span className="profile-value">{user.phone}</span>
            </div>
            <div className="profile-field">
              <span className="profile-label">Address</span>
              <span className="profile-value">{user.address || "—"}</span>
            </div>
            <div className="profile-field">
              <span className="profile-label">Joined</span>
              <span className="profile-value">
                {user.createdAt
                  ? new Date(user.createdAt).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "—"}
              </span>
            </div>
          </div>

          {/* Projects */}
          <div className="profile-projects">
            <span className="profile-label">Projects</span>
            <div className="profile-project-chips">
              {Array.isArray(user.project) && user.project.length > 0 ? (
                user.project.map((p) => {
                  const label =
                    typeof p === "string"
                      ? p
                      : p.projectName || p.name || p._id || "Project";
                  return (
                    <span key={p._id || label} className="badge badge-chip">
                      {label}
                    </span>
                  );
                })
              ) : (
                <span className="profile-value">No projects assigned</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
